import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { guideArticles, type LocalizedText } from '@/data/articles';

const GUIDE_IMG =
  'https://readdy.ai/api/search-image?query=Organized%20product%20catalog%20spreadsheet%20concept%20with%20sneaker%20and%20clothing%20thumbnail%20cards%20arranged%20in%20a%20clean%20grid%20on%20a%20warm%20cream%20background%20soft%20sage%20green%20accents%20minimal%20editorial%20style%20high%20detail%20fresh%20aesthetic&width=800&height=600&seq=basetao-guide-01&orientation=landscape';

function pick(text: LocalizedText, lang: string): string {
  const key = lang as keyof LocalizedText;
  return text[key] ?? text.en;
}

export default function TopicsSection() {
  const { t } = useTranslation();
  const { lang = 'en' } = useParams();

  return (
    <section id="topics" className="py-16 md:py-24 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">
            {t('topics.title')}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base">{t('topics.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Link
            to={`/${lang}/basetao-spreadsheet-guide`}
            className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
          >
            <div className="w-full h-44 overflow-hidden">
              <img
                src={GUIDE_IMG}
                alt="Basetao spreadsheet guide"
                title="Basetao spreadsheet guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <span className="inline-flex self-start rounded-full bg-accent-100 text-accent-800 text-xs font-semibold px-3 py-1 mb-3">
                {t('nav.guides')}
              </span>
              <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                {t('topics.cardTitle')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {t('topics.cardDesc')}
              </p>
              <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary-600 group-hover:text-primary-700 whitespace-nowrap">
                {t('topics.readMore')}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </span>
            </div>
          </Link>

          {guideArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/${lang}/guide/${article.slug}`}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src={article.image}
                  alt={pick(article.h1, lang)}
                  title={pick(article.h1, lang)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <span className="inline-flex self-start rounded-full bg-accent-100 text-accent-800 text-xs font-semibold px-3 py-1 mb-3">
                  {pick(article.tag, lang)}
                </span>
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                  {pick(article.h1, lang)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                  {pick(article.metaDescription, lang)}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary-600 group-hover:text-primary-700 whitespace-nowrap">
                  {t('topics.readMore')}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}